import React, { useState, useEffect, useContext, useRef } from "react";
import { faCheck, faTimes, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import CheckoutContext from "../contexts/CheckoutContext";
import axios from "axios";
import LoadingSpinner from "./LoadingSpinner";

const inputCls = (valid, value, touched) => [
    "w-full px-3 py-2.5 rounded-xl border bg-white outline-none transition-all text-[16px] text-[#2a1419]",
    touched && value && !valid
        ? "border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100"
        : valid
            ? "border-[#a02338] focus:border-[#a02338] focus:ring-2 focus:ring-[#fbedef]"
            : "border-gray-200 focus:border-[#a02338] focus:ring-2 focus:ring-[#fbedef]",
].join(" ");

function FieldRow({ label, id, valid, value, touched, note, children }) {
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
                <label htmlFor={id} className="text-[13px] font-medium text-[#2a1419]">
                    {label}
                </label>
                {valid && value && <FontAwesomeIcon icon={faCheck} className="text-[#a02338] text-[11px]" />}
                {touched && !valid && value && <FontAwesomeIcon icon={faTimes} className="text-red-400 text-[11px]" />}
            </div>
            {children}
            {touched && value && !valid && (
                <p className="text-[11px] text-red-500 flex items-center gap-1 mt-0.5">
                    <FontAwesomeIcon icon={faInfoCircle} />
                    {note}
                </p>
            )}
        </div>
    );
}

function SellerForm() {
    const navigate = useNavigate();
    const formRef = useRef(null);
    const { setSeller } = useContext(CheckoutContext);

    const [localSeller, setLocalSeller] = useState("");
    const [validLocalSeller, setValidLocalSeller] = useState(false);
    const [touched, setTouched] = useState(false);
    const [sellerUsers, setSellerUsers] = useState([]);
    const [loadingSellers, setLoadingSellers] = useState(true);

    const [errMsg, setErrMsg] = useState("");

    async function getSellers() {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}user/seller`);
            setSellerUsers(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingSellers(false);
        }
    }

    function handleClickContinue(e) {
        e.preventDefault();
        if (localSeller === "Otro") {
            setValidLocalSeller(true);
        } else {
            const isValid = sellerUsers.some((seller) => seller.id == localSeller);
            if (!isValid) {
                setErrMsg("Selecciona una opción válida");
                return;
            }
        }

        setSeller({ id: localSeller });
        navigate("/checkout/info");
    }

    useEffect(() => {
        getSellers();
    }, []);

    useEffect(() => {
        if (localSeller === "Otro") {
            setValidLocalSeller(true);
        } else if (sellerUsers.length > 0) {
            setValidLocalSeller(sellerUsers.some((seller) => seller.id == localSeller));
        }
        setErrMsg("");
    }, [localSeller, sellerUsers]);

    if (loadingSellers) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div ref={formRef} className="mx-3 mt-4 mb-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

            {/* Header */}
            <div className="px-4 py-3 bg-[#f7f4ef] border-b border-gray-100 flex items-center gap-2">
                <svg className="w-4 h-4 text-[#a02338]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                <p className="text-sm font-semibold text-[#2a1419]">¿Quién te envió el link de compra?</p>
            </div>

            <form onSubmit={handleClickContinue} className="px-4 py-4 flex flex-col gap-4">

                {errMsg && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center gap-2">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-amber-500 text-[13px] flex-shrink-0" />
                        <p className="text-[12px] text-amber-700">{errMsg}</p>
                    </div>
                )}

                <FieldRow
                    label="Vendedor"
                    id="seller"
                    valid={validLocalSeller}
                    value={localSeller}
                    touched={touched}
                    note='Selecciona "Otro" si no encuentras tu opción'
                >
                    <select
                        className={inputCls(validLocalSeller, localSeller, touched)}
                        id="seller"
                        autoComplete="off"
                        onChange={(e) => { setLocalSeller(e.target.value); setTouched(true); }}
                        required
                    >
                        <option value="">Seleccionar vendedor</option>
                        {sellerUsers.map((seller) => (
                            <option key={seller.id} value={seller.id}>{seller.firstName} {seller.lastName}</option>
                        ))}
                        <option value="Otro">Otro</option>
                    </select>
                </FieldRow>

                <input
                    type="submit"
                    disabled={!validLocalSeller}
                    value={validLocalSeller ? "Continuar →" : "Selecciona una opción"}
                    className={`
                        w-full py-3 rounded-xl text-sm font-semibold mt-1 transition-all duration-150 cursor-pointer
                        ${!validLocalSeller
                            ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                            : "bg-[#8c1d2f] hover:bg-[#a82a42] text-[#f5f0e8] shadow-md shadow-[#8c1d2f]/20"
                        }
                    `}
                />
            </form>
        </div>
    );
}

export default SellerForm;
